import React, { useEffect, useRef } from 'react';
import './spaceBackground.scss';

const SpaceBackground = () => {
    const canvasRef = useRef(null);
    const shootingStarsRef = useRef([]);
    const animationFrameRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;

        const ctx = canvas.getContext('2d');

        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };

        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);

        const createShootingStar = () => ({
            x: Math.random() * canvas.width,
            y: Math.random() * (canvas.height * 0.5),
            length: Math.random() * 80 + 50,
            speed: Math.random() * 8 + 6,
            angle: Math.PI / 4 + (Math.random() * 0.2 - 0.1),
            opacity: 1,
            trail: [],
            active: true,
        });

        const updateStar = (star) => ({
            ...star,
            x: star.x + Math.cos(star.angle) * star.speed,
            y: star.y + Math.sin(star.angle) * star.speed,
            active: !(star.x + Math.cos(star.angle) * star.speed > canvas.width + 100
                || star.y + Math.sin(star.angle) * star.speed > canvas.height + 100),
        });

        const drawShootingStar = (star) => {
            if (!star.active) return;

            const tailX = star.x - Math.cos(star.angle) * star.length;
            const tailY = star.y - Math.sin(star.angle) * star.length;

            const gradient = ctx.createLinearGradient(star.x, star.y, tailX, tailY);
            gradient.addColorStop(0, `rgba(255, 255, 255, ${star.opacity})`);
            gradient.addColorStop(0.3, `rgba(225, 191, 64, ${star.opacity * 0.7})`);
            gradient.addColorStop(1, 'rgba(225, 191, 64, 0)');

            ctx.beginPath();
            ctx.moveTo(star.x, star.y);
            ctx.lineTo(tailX, tailY);
            ctx.strokeStyle = gradient;
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';
            ctx.stroke();

            ctx.beginPath();
            ctx.arc(star.x, star.y, 2, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity})`;
            ctx.fill();
        };

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            if (Math.random() < 0.02 && shootingStarsRef.current.length < 5) {
                shootingStarsRef.current.push(createShootingStar());
            }

            shootingStarsRef.current.forEach((star) => {
                drawShootingStar(star);
            });

            shootingStarsRef.current = shootingStarsRef.current
                .map(updateStar)
                .filter((star) => star.active);

            animationFrameRef.current = window.requestAnimationFrame(animate);
        };

        animate();

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            if (animationFrameRef.current) {
                window.cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    return (
        <div className="space-background">
            {/* Aurora/Nebula glow layer */}
            <div className="nebula-container">
                <div className="nebula nebula-1" />
                <div className="nebula nebula-2" />
            </div>

            {/* Distant galaxies */}
            <div className="galaxies-container">
                <div className="galaxy galaxy-1" />
                <div className="galaxy galaxy-2" />
                <div className="galaxy galaxy-3" />
            </div>

            {/* Constellations */}
            <div className="constellations-container">
                {/* Orion-like constellation */}
                <svg className="constellation constellation-1" viewBox="0 0 100 120">
                    <line x1="50" y1="10" x2="30" y2="35" />
                    <line x1="50" y1="10" x2="70" y2="35" />
                    <line x1="30" y1="35" x2="70" y2="35" />
                    <line x1="35" y1="55" x2="65" y2="55" />
                    <line x1="30" y1="35" x2="35" y2="55" />
                    <line x1="70" y1="35" x2="65" y2="55" />
                    <line x1="35" y1="55" x2="20" y2="90" />
                    <line x1="65" y1="55" x2="80" y2="90" />
                    <circle cx="50" cy="10" r="2" />
                    <circle cx="30" cy="35" r="1.5" />
                    <circle cx="70" cy="35" r="1.5" />
                    <circle cx="35" cy="55" r="1" />
                    <circle cx="50" cy="55" r="1" />
                    <circle cx="65" cy="55" r="1" />
                    <circle cx="20" cy="90" r="1.5" />
                    <circle cx="80" cy="90" r="1.5" />
                </svg>

                {/* Big Dipper-like constellation */}
                <svg className="constellation constellation-2" viewBox="0 0 120 60">
                    <line x1="10" y1="30" x2="30" y2="25" />
                    <line x1="30" y1="25" x2="45" y2="35" />
                    <line x1="45" y1="35" x2="60" y2="30" />
                    <line x1="60" y1="30" x2="75" y2="20" />
                    <line x1="75" y1="20" x2="95" y2="15" />
                    <line x1="95" y1="15" x2="110" y2="25" />
                    <circle cx="10" cy="30" r="1.5" />
                    <circle cx="30" cy="25" r="1.5" />
                    <circle cx="45" cy="35" r="2" />
                    <circle cx="60" cy="30" r="1.5" />
                    <circle cx="75" cy="20" r="1.5" />
                    <circle cx="95" cy="15" r="1.5" />
                    <circle cx="110" cy="25" r="2" />
                </svg>

                {/* Small triangle constellation */}
                <svg className="constellation constellation-3" viewBox="0 0 50 50">
                    <line x1="25" y1="5" x2="5" y2="45" />
                    <line x1="5" y1="45" x2="45" y2="45" />
                    <line x1="45" y1="45" x2="25" y2="5" />
                    <circle cx="25" cy="5" r="2" />
                    <circle cx="5" cy="45" r="1.5" />
                    <circle cx="45" cy="45" r="1.5" />
                </svg>
            </div>

            <canvas ref={canvasRef} className="shooting-stars-canvas" />
            <div className="planets-container">
                {/* Sun - bright yellow/orange, largest */}
                <div className="planet planet-sun">
                    <div className="planet-glow" />
                </div>
                {/* Mercury - small grey planet */}
                <div className="planet planet-mercury">
                    <div className="planet-glow" />
                </div>
                {/* Venus - yellowish-white planet */}
                <div className="planet planet-venus">
                    <div className="planet-glow" />
                </div>
                {/* Mars - red planet */}
                <div className="planet planet-mars">
                    <div className="planet-glow" />
                </div>
                {/* Jupiter - large orange/tan planet */}
                <div className="planet planet-jupiter">
                    <div className="planet-glow" />
                </div>
                {/* Saturn - yellow with ring */}
                <div className="planet planet-saturn">
                    <div className="planet-glow" />
                </div>
            </div>
        </div>
    );
};

export default SpaceBackground;
